import { flash, SubmitButton } from "@xola/ui-kit";
import { nullableString } from "common";
import React from "react";
import { useMutation } from "react-query";
import { Experience } from "types";
import { boolean, object, string } from "yup";
import { api } from "../../../api/api";
import { ScrollToFieldError } from "../../../helpers/validationHelper";
import { useSeller } from "../../../hooks/useAccount";
import { useAutoFetchCollection } from "../../../hooks/useAutoFetchCollection";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { ContentLayout } from "../../Layout/ContentLayout";
import { Loader } from "../../Loader";
import { TermsForm } from "../TermsForm";
import { NoOverrides, OverridingExperiencesList } from "./OverridingExperiencesList";

const validationSchema = object().shape({
    termsLegal: string()
        .nullable()
        .when(["termsHuman", "forceAgree"], {
            is: (termsHuman: string, forceAgree: boolean) => termsHuman || forceAgree,
            then: nullableString().required(),
        }),
    termsHuman: nullableString(),
    forceAgree: boolean().nullable(),
});

export const GlobalProductTerms = () => {
    const invalidate = useInvalidate();
    const seller = useSeller();
    const { data: experiences, isLoading } = useAutoFetchCollection<Experience>("experiences");
    const termsOverridingExperiences = experiences.filter((experience: Experience) => experience.terms);

    const { mutateAsync: remove } = useMutation(async () => {
        try {
            const url = `/sellers/${seller.id}/terms`;
            return await api.delete(url);
        } catch {
            flash.danger("Error deleting terms");
            return null;
        }
    });

    const { mutateAsync: save } = useMutation(async (payload: any) => {
        try {
            const url = `/sellers/${seller.id}/terms`;
            return await api.post(url, payload);
        } catch {
            flash.danger("Error updating terms");
            return null;
        }
    });

    const onSave = async (values: any) => {
        await (!values.termsHuman && !values.termsLegal && !values.forceAgree ? remove() : save(values));
        invalidate("sellers", seller.id);
        flash.success("Successfully updated terms");
    };

    return (
        <ContentLayout isWide>
            <XolaFormik
                enableReinitialize
                validateOnChange
                validateOnBlur={false}
                validationSchema={validationSchema}
                initialValues={{ ...seller.terms }}
                onSubmit={onSave}
            >
                {({ values, setFieldValue, isSubmitting, handleSubmit, handleReset }: any) => {
                    return (
                        <>
                            <h2 className="mt-4">Global Terms and Conditions</h2>
                            <ContentLayout.Section title="Global Terms and Conditions">
                                <div className="flex flex-grow flex-col space-y-5">
                                    <TermsForm values={values} setFieldValue={setFieldValue} />
                                    <ContentLayout.Footer preferenceSidebar>
                                        <SubmitButton
                                            className="self-start"
                                            isLoading={isSubmitting}
                                            onClick={handleSubmit}
                                        >
                                            Save
                                        </SubmitButton>
                                        <SecondaryButton type="reset" onClick={handleReset}>
                                            Cancel
                                        </SecondaryButton>
                                    </ContentLayout.Footer>
                                </div>
                            </ContentLayout.Section>

                            <ScrollToFieldError />
                        </>
                    );
                }}
            </XolaFormik>

            <ContentLayout.Section
                title="Overrides"
                description="These products have overridden the global terms and conditions set above"
            >
                <div className="flex space-x-8 border-t border-gray-light pt-10">
                    <div className="w-full max-w-[560px]">
                        {isLoading ? (
                            <Loader size="tiny" className="p-2" text="Loading Experiences" />
                        ) : (
                            <div>
                                {termsOverridingExperiences.length > 0 ? (
                                    <OverridingExperiencesList experiences={termsOverridingExperiences} />
                                ) : (
                                    <NoOverrides />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </ContentLayout.Section>
        </ContentLayout>
    );
};
